import { MoreHorizontal } from 'lucide-react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Button } from '../atoms/button';
import { cn } from '../lib/utils';

export interface DropdownMenuItem {
  readonly label: string;
  readonly isDestructive?: boolean;
  readonly onSelect: () => void;
}

export interface DropdownMenuProps {
  /** 트리거와 메뉴가 공유하는 접근성 라벨 — 관례상 `${대상 이름} 메뉴`. */
  readonly ariaLabel: string;
  readonly items: readonly DropdownMenuItem[];
}

/** 미트볼(⋯) 버튼으로 여닫는 소형 드롭다운 메뉴 — 바깥 클릭·Escape 로 닫힌다. */
export const DropdownMenu = ({ ariaLabel, items }: DropdownMenuProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const container = useRef<HTMLSpanElement>(null);

  // 자신의 트리거·메뉴 안쪽 클릭은 유지하고, 다른 메뉴를 포함한 바깥 클릭만 닫는다.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const close = (event: MouseEvent): void => {
      if (event.target instanceof Node && !container.current?.contains(event.target))
        setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', close);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  return (
    <span ref={container} className="relative">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <MoreHorizontal aria-hidden="true" />
      </Button>

      {isOpen ? (
        <div
          role="menu"
          aria-label={ariaLabel}
          className="absolute right-0 top-7 z-40 w-28 rounded-md border bg-background p-1"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={cn(
                'block w-full rounded-sm px-3 py-1.5 text-left text-sm hover:bg-accent',
                item.isDestructive && 'text-destructive',
              )}
              onClick={() => {
                setIsOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
};
